import { Dispatch, FormEvent, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { Box, Button, DialogTitle, Input } from '@mui/material';
import { darken } from 'polished';
import styled from 'styled-components';

import { createViewer, editPublicLinkViewers } from '@/features/publicLinks/api';
import { useShowToast } from '@/hooks/useShowToast';
import { Error } from '@/types/api';

import { getTenantEntities, TenantEntities } from '../../api/tenant-sso';

interface PublishDialogFormSsoProps {
  isAccessPage: boolean;
  isDisabled: boolean;
  selectedEntities: TenantEntities;
  setSelectedEntities: Dispatch<SetStateAction<TenantEntities>>;
  id?: number;
}

const PublishDialogFormSso = ({
  selectedEntities,
  setSelectedEntities,
  isDisabled,
  isAccessPage,
  id,
}: PublishDialogFormSsoProps) => {
  const { showToast } = useShowToast();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { data: entities = { groups: [], viewers: [] }, refetch } = useQuery(
    getTenantEntities.name,
    () => getTenantEntities(),
    { onSuccess: data => updateTenantEntities(data) }
  );
  const [tenantEntities, setTenantEntities] = useState<TenantEntities>({ groups: [], viewers: [] });
  const [owner, setOwner] = useState<TenantEntities['viewers'][number]>();
  const [selected, setSelected] = useState<TenantEntities>({ groups: [], viewers: [] });
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const [searchValue, setSearchValue] = useState('');

  const { mutateAsync: createViewerMutation } = useMutation(
    ({ email }: { email: string }) => createViewer({ email }),
    {
      onSuccess: (data: TenantEntities['viewers'][number]) => {
        setSelectedEntities(prev => ({ ...prev, viewers: [...prev.viewers, data] }));
        setSearchValue('');
        setIsOpen(false);
      },
      onError: (err: Error) => {
        showToast('error', err.response.data.message);
      },
    }
  );

  const handleCreateViewer = async (e: FormEvent) => {
    e.preventDefault();

    await createViewerMutation({ email: searchValue });
  };

  const filteredEntityData = useMemo(
    () => ({
      groups: tenantEntities.groups.filter(item =>
        item.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
      viewers: tenantEntities.viewers.filter(
        item =>
          `${item.name} ${item.lastname}`.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.email.toLowerCase().includes(searchValue.toLowerCase())
      ),
    }),
    [tenantEntities, searchValue]
  );

  const listRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }

    const entityId = (event.target as HTMLElement)?.id;
    const groupMatch = entityId.match(/^add-group#(\d+)$/);
    const viewerMatch = entityId.match(/^add-viewer#(\d+)$/);

    if (groupMatch) {
      const groupId = groupMatch[1];
      void handleAddEntity(tenantEntities.groups.find(item => item.id === Number(groupId))!);
    } else if (viewerMatch) {
      const viewerId = viewerMatch[1];
      void handleAddEntity(tenantEntities.viewers.find(item => item.id === Number(viewerId))!);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const { mutateAsync } = useMutation(
    ({ publicLinkId, updateEntities }: { publicLinkId: number; updateEntities: TenantEntities }) =>
      editPublicLinkViewers(publicLinkId, updateEntities)
  );

  const updateTenantEntities = (updatedEntities?: TenantEntities) => {
    const ENTITIES = updatedEntities ?? entities;

    const updatedGroups = ENTITIES.groups.filter(
      entity => !selectedEntities.groups?.some(item => item.id === entity.id)
    );
    const updatedViewers = ENTITIES.viewers.filter(
      entity => !selectedEntities.viewers?.some(item => item.id === entity.id)
    );
    setOwner(selectedEntities.viewers.find(item => item?.isOwner));
    setSelected({
      ...selectedEntities,
      viewers: selectedEntities.viewers.filter(item => !item?.isOwner),
    });
    setTenantEntities({ groups: updatedGroups, viewers: updatedViewers });
  };

  useEffect(() => {
    setSelected(selectedEntities);
    if (selectedEntities.groups?.length > 0 || selectedEntities.viewers?.length > 0) {
      updateTenantEntities();
    } else {
      setTenantEntities(entities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntities]);

  const handleAddEntity = async (
    entity: TenantEntities['groups'][number] | TenantEntities['viewers'][number]
  ) => {
    if ('email' in entity) {
      setSelectedEntities(prev => ({ ...prev, viewers: [...(prev?.viewers ?? []), entity] }));
      if (isAccessPage) {
        await mutateAsync({
          publicLinkId: Number(id),
          updateEntities: { ...selected, viewers: [...selected.viewers, entity] },
        });
      }
    } else {
      setSelectedEntities(prev => ({ ...prev, groups: [...(prev?.groups ?? []), entity] }));
      if (isAccessPage) {
        await mutateAsync({
          publicLinkId: Number(id),
          updateEntities: { ...selected, groups: [...selected.groups, entity] },
        });
      }
    }
  };

  const handleRemoveEntity = async (
    entity: TenantEntities['groups'][number] | TenantEntities['viewers'][number]
  ) => {
    if ('email' in entity) {
      setSelectedEntities(prev => ({
        ...prev,
        viewers: prev?.viewers.filter(item => item.id !== entity.id),
      }));
      if (isAccessPage) {
        await mutateAsync({
          publicLinkId: Number(id),
          updateEntities: {
            ...selected,
            viewers: selected.viewers.filter(item => item.id !== entity.id),
          },
        });
      }
    } else {
      setSelectedEntities(prev => ({
        ...prev,
        groups: prev?.groups.filter(item => item.id !== entity.id),
      }));
      if (isAccessPage) {
        await mutateAsync({
          publicLinkId: Number(id),
          updateEntities: {
            ...selected,
            groups: selected.groups.filter(item => item.id !== entity.id),
          },
        });
      }
    }
  };

  const handleRemoveAll = () => {
    setSelectedEntities({
      groups: [],
      viewers: selectedEntities.viewers.filter(item => item?.isOwner),
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '3rem',
        borderRadius: '2rem',
        wordBreak: 'break-word',
      }}
    >
      <SSOTitleWrapper>
        <SSoTitle>
          <StyledSSOTitle>{t('editor.header.publish_dialog.link_access')}</StyledSSOTitle>
          <p>{t('editor.header.publish_dialog.link_access_description')}</p>
        </SSoTitle>
        {!isDisabled && (
          <RemoveAllButton type="button" onClick={handleRemoveAll}>
            {t('editor.header.publish_dialog.remove_all')}
          </RemoveAllButton>
        )}
      </SSOTitleWrapper>
      <ListWrapper style={{ maxHeight: '150px', overflowY: 'auto' }}>
        {owner && (
          <SelectedListItemWrapper>
            <SelectedListItem>
              <p>{owner.name}</p>
              <p>{owner.email}</p>
            </SelectedListItem>
            <EndItemWrapper>
              <Owner>{t('public_links.owner')}</Owner>
            </EndItemWrapper>
          </SelectedListItemWrapper>
        )}
        {(selected.groups?.length ?? 0) === 0 && (selected.viewers?.length ?? 0) === 0 && (
          <EmptySelectedEntity>
            {t('editor.header.publish_dialog.anyone_with_link')}
          </EmptySelectedEntity>
        )}
        {selected.groups?.map(item => (
          <SelectedListItemWrapper key={item.id}>
            <SelectedListItem>
              <p>{item.name}</p>
              <p style={{ fontStyle: 'italic' }}>{t('public_links.group')}</p>
            </SelectedListItem>
            <EndItemWrapper>
              {!isDisabled && (
                <TrashBinWrapper type="button" onClick={() => handleRemoveEntity(item)}>
                  <TrashIcon width={15} height={15} />
                </TrashBinWrapper>
              )}
            </EndItemWrapper>
          </SelectedListItemWrapper>
        ))}
        {selected.viewers?.map(item => (
          <SelectedListItemWrapper key={item.id}>
            <SelectedListItem>
              {item?.name ||
                (item?.lastname && (
                  <p>
                    {item.name} {item.lastname}
                  </p>
                ))}
              <p>{item.email}</p>
            </SelectedListItem>
            <EndItemWrapper>
              {!isDisabled && (
                <TrashBinWrapper type="button" onClick={() => handleRemoveEntity(item)}>
                  <TrashIcon width={15} height={15} />
                </TrashBinWrapper>
              )}
            </EndItemWrapper>
          </SelectedListItemWrapper>
        ))}
      </ListWrapper>
      <AddViewerButtonWrapper ref={listRef}>
        <form onSubmit={handleCreateViewer}>
          <AddViewerInputWrapper>
            <AddViewerInput
              type="email"
              sx={{
                '&.MuiInput-root:before': {
                  content: 'unset',
                },
                '&.MuiInput-root:after': {
                  border: 'none',
                },
                '&.Mui-disabled': {
                  content: 'unset',
                },
              }}
              onClick={() => setIsOpen(true)}
              placeholder={t('editor.header.publish_dialog.add_viewer')}
              onChange={e => setSearchValue(e.currentTarget.value)}
              value={searchValue}
            />
            <ChevronDownIcon width={20} height={20} />
          </AddViewerInputWrapper>
          <Button style={{ display: 'none' }} ref={buttonRef} type="submit" />
        </form>
        {isOpen && (
          <ViewerList>
            {filteredEntityData.groups.length <= 0 &&
            filteredEntityData.viewers.length <= 0 &&
            searchValue?.length > 0 ? (
              <AddNewViewer onClick={() => buttonRef.current?.click()}>
                {t('editor.header.publish_dialog.add_viewer_with_email')}
              </AddNewViewer>
            ) : (
              <>
                <ListTitle>{t('editor.header.publish_dialog.groups')}</ListTitle>
                <BorderListWrapper>
                  {filteredEntityData.groups.length > 0 ? (
                    filteredEntityData.groups.map(item => (
                      <ListItem key={item.id} id={`add-group#${item.id}`}>
                        {item.name}
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>{t('empty_list')}</ListItem>
                  )}
                </BorderListWrapper>
                <ListTitle>{t('editor.header.publish_dialog.users')}</ListTitle>
                <ListWrapper>
                  {filteredEntityData.viewers.length > 0 ? (
                    filteredEntityData.viewers.map(item => (
                      <ListItem key={item.id} id={`add-viewer#${item.id}`}>
                        {item?.name || item?.lastname
                          ? `${item.name} ${item.lastname}`
                          : item.email}
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>{t('empty_list')}</ListItem>
                  )}
                </ListWrapper>
              </>
            )}
          </ViewerList>
        )}
      </AddViewerButtonWrapper>
    </Box>
  );
};

const AddViewerInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => darken(0.1, theme.palette.brand.backgroundDialog)};
  box-shadow: ${({ theme }) => theme.shadows[0]};
  border-radius: 2rem;
  width: 100%;
  padding: 1rem 1.5rem;
  transition: all ease-in-out 300ms;

  :hover {
    background-color: ${({ theme }) => darken(0.2, theme.palette.brand.backgroundDialog)};
  }
`;

const Owner = styled.p`
  font-style: italic;
`;

const EndItemWrapper = styled.div`
  min-width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptySelectedEntity = styled.p`
  font-size: 1.5rem;
  margin: 1rem 0.5rem;
`;

const SSoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  width: 100%;
`;

const SSOTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-bottom: 2px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
`;

const RemoveAllButton = styled.button`
  font-size: 1rem;
  font-style: italic;
  white-space: nowrap;
`;

const StyledSSOTitle = styled(DialogTitle)`
  font-size: 2rem;
  color: ${({ theme }) => theme.palette.brand.primary};
  font-weight: 700;
  padding: 0;
`;

const AddViewerButtonWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const AddViewerInput = styled(Input)`
  font-size: 1.5rem;
  font-weight: 500;
  width: 100%;
  color: ${({ theme }) => theme.palette.brand.primary};
  position: relative;
  height: 100%;
  border: none;
`;

const ViewerList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: max-content;
  max-height: 250px;
  position: absolute;
  left: 0;
  top: calc(100% + 10px);
  z-index: 100;
  border-radius: 10px;
  padding: 20px;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows['19']};
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const ListTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.action.activatedOpacity};
  margin-bottom: 0.5rem;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const BorderListWrapper = styled(ListWrapper)`
  border-bottom: 2px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  margin-bottom: 2rem;
`;

const ListItem = styled(Button)`
  font-size: 1.25rem;
  text-transform: none;
  justify-content: start;
  width: 100%;
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  color: ${({ theme }) => theme.palette.brand.primary};

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const SelectedListItem = styled.div`
  font-size: 1.25rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  width: 100%;
  color: ${({ theme }) => theme.palette.brand.primary};
`;

const SelectedListItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const TrashBinWrapper = styled.button`
  color: ${({ theme }) => theme.palette.brand.primary};
  padding: 0.5rem;
  width: max-content;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 300ms ease-in-out;
  border-radius: 5px;

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const AddNewViewer = styled(ListItem)`
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  padding: 0.5rem 1rem;
  font-weight: 600;
`;

export default PublishDialogFormSso;
